/**
 * Did the hand-off email actually reach each person an item is with?
 *
 * Case round, finding 1: an item sent by a loosely typed name ("jhon fauth",
 * "emily") was never emailed, yet the header said "With John Fauth, Roblyn
 * now. It is theirs to decide." The only proof was a timeline line near the
 * bottom. The delivery outcome reaches the door as thread timeline events
 * (`route_delivered` / `route_delivery_failed`, written by the reroute's
 * delivery step), so the outcome per person is read from them here.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorHandoffRead, DoorThreadEventRead, DoorThreadRead } from '../../services/qcpInvestigations';
import { nameTokens } from '../../components/portal/threadTriageView';

export type DeliveryState =
  /** The newest delivery line about them says the email went out. */
  | 'delivered'
  /** The newest delivery line about them is a failure, and nothing
   *  visible came after it. */
  | 'failed'
  /** No line the screen can trust says either way. */
  | 'unconfirmed';

export interface PersonDelivery {
  /** The person as the item names them ("John Fauth"). */
  name: string;
  state: DeliveryState;
  delivered: boolean;
  /** Why not, in plain words; null unless failed. */
  reason: string | null;
}

export interface DeliveryOutcome {
  people: PersonDelivery[];
  /** The people whose email failed, as the latest thing the screen can see
   *  (the only ones the red alarm names). */
  undelivered: PersonDelivery[];
  /** The people nothing confirms either way. */
  unconfirmed: PersonDelivery[];
  /** Everyone on the item failed. */
  nobodyDelivered: boolean;
}

/** "John Fauth, Roblyn" / "Emily / Roblyn" / "Laura & Hadi" → one name each. */
export function routeeNames(routedTo: string | null | undefined): string[] {
  return (routedTo ?? '')
    .split(/\s*(?:,|;|\/|&|\band\b)\s*/i)
    .map((n) => n.trim())
    .filter(Boolean);
}

/** The name an "Emailed to …" line was sent to. */
function deliveredName(summary: string): string | null {
  const m = /^Emailed to (.+?)(?: \(|$| as the )/.exec(summary.trim());
  return m ? m[1].trim() : null;
}

/** The typed text or name a failed line was about. */
function failedSegments(summary: string): string[] {
  const quoted = [...summary.matchAll(/'([^']+)'/g)].map((m) => m[1]);
  if (quoted.length > 0) return quoted;
  const m = /^Email to (.+?) failed/.exec(summary.trim());
  return m ? [m[1]] : [];
}

/** Every word of the shorter name is in the longer one ("Roblyn" ⊂ "Roblyn Jericho"). */
export function sameName(a: string, b: string): boolean {
  const ta = nameTokens(a);
  const tb = nameTokens(b);
  if (ta.length === 0 || tb.length === 0) return false;
  const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  return short.every((t) => long.includes(t));
}

/** At least one word in common ("jhon fauth" ~ "John Fauth"). */
function overlaps(a: string, b: string): boolean {
  const tb = nameTokens(b);
  return nameTokens(a).some((t) => tb.includes(t));
}

function failureReason(summary: string, segment: string | null): string {
  if (/^No staff match/i.test(summary)) {
    return segment
      ? `the name typed (“${segment.trim()}”) matched nobody in the staff list, so no email went out`
      : 'the name typed matched nobody in the staff list, so no email went out';
  }
  if (/^Couldn't check the staff directory/i.test(summary)) {
    return 'the staff list could not be checked when it was sent, so no email went out';
  }
  if (/^Email to .+ failed/i.test(summary)) return 'the email to them failed to send';
  if (/^Not emailed/i.test(summary)) {
    return summary.replace(/^Not emailed\s*[—-]\s*/i, '').trim() || 'no email went out';
  }
  return 'no email went out';
}

/** Slack between a send and the lines it writes. */
const SAME_SEND_MS = 60_000;

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

/**
 * What the screen holds of an item's history: the rows of the pages loaded
 * so far, the order they were read in, and whether the server said rows
 * exist past them (`has_more` of the last page read).
 */
export interface TimelineWindow<E = Pick<DoorThreadEventRead, 'event_type' | 'summary'> & { created_at?: string }> {
  events: ReadonlyArray<E>;
  order: 'asc' | 'desc';
  has_more: boolean;
}

/**
 * Does this window hold the item's NEWEST rows? Read newest first it always
 * does (rows missing are older ones); read oldest first only when nothing
 * is past it. When it does not, nothing may be read from it as "the
 * latest" — a later re-send can be among the missing rows (QT-10049).
 */
export function timelineIsCurrent(window: Pick<TimelineWindow, 'order' | 'has_more'>): boolean {
  return window.order === 'desc' || !window.has_more;
}

/**
 * The delivery outcome for an item sent to someone, per person. Null when
 * the window holds no delivery line at all.
 *
 * A failure is claimed only when it is the newest thing that can be known:
 * the window holds the item's newest rows (timelineIsCurrent — so a later
 * re-send would be in it), and no later send (routed_at) came after the
 * delivery lines (a send whose outcome is still being written). Otherwise
 * everyone not confirmed delivered is 'unconfirmed' — never a red alarm.
 */
export function deliveryOutcome(
  thread: Pick<DoorThreadRead, 'routed_to'> & Partial<Pick<DoorThreadRead, 'routed_at'>>,
  window: TimelineWindow | null | undefined,
): DeliveryOutcome | null {
  const names = routeeNames(thread.routed_to);
  if (names.length === 0 || !window) return null;
  const lines = window.events
    .map((e, i) => ({ e, i, at: ms(e.created_at) }))
    .filter(({ e }) => e.event_type === 'route_delivered' || e.event_type === 'route_delivery_failed');
  if (lines.length === 0) return null;

  // Can the screen see the latest send's outcome?
  const lastLine = Math.max(...lines.map((l) => l.at).filter((t) => !Number.isNaN(t)));
  const routed = ms(thread.routed_at);
  const laterSend = !Number.isNaN(routed) && Number.isFinite(lastLine) && routed - lastLine > SAME_SEND_MS;
  const current = timelineIsCurrent(window) && !laterSend;

  // Newest line first (time, then position — the same send writes several).
  // (A newest-first window lists the newer of two same-instant lines first.)
  const later = window.order === 'desc' ? -1 : 1;
  const newest = [...lines].sort((a, b) => (b.at || 0) - (a.at || 0) || later * (b.i - a.i));

  const people = names.map((name): PersonDelivery => {
    const latest = newest.find(({ e }) => {
      if (e.event_type === 'route_delivered') {
        const to = deliveredName(e.summary);
        return to !== null && sameName(to, name);
      }
      return failedSegments(e.summary).some((s) => overlaps(s, name));
    });
    if (latest?.e.event_type === 'route_delivered') {
      return { name, state: 'delivered', delivered: true, reason: null };
    }
    if (latest && current) {
      const segment = failedSegments(latest.e.summary).find((s) => overlaps(s, name)) ?? null;
      return { name, state: 'failed', delivered: false, reason: failureReason(latest.e.summary, segment) };
    }
    return { name, state: 'unconfirmed', delivered: false, reason: null };
  });
  const undelivered = people.filter((p) => p.state === 'failed');
  return {
    people,
    undelivered,
    unconfirmed: people.filter((p) => p.state === 'unconfirmed'),
    nobodyDelivered: undelivered.length === people.length,
  };
}

/** "John Fauth and Emily Adams" / "A, B and C". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The system assigned this item to someone and never emailed them (card
 * 75bf026f): the backend's route email audit found no send about anyone it
 * is with now. Merlin's nightly router writes routes by raw SQL — a thread
 * born routed (QT-10055) or re-pointed at new people (QT-10016) — and that
 * path sends nothing, so the people named do not know they hold it. The
 * timeline has no delivery line for it either, which is why the per-person
 * outcome above cannot see it.
 */
export function neverEmailed(thread: Pick<DoorThreadRead, 'route_notified'>): boolean {
  return thread.route_notified === 'not_sent';
}

/**
 * The hand-offs the door gate is still holding (kanban 8a4d2212): waiting
 * for Merlin's "already known?" check, or held because Merlin judged the
 * person already knows. These are the ones the screen offers Send anyway
 * on; sent / failed / superseded entries read from the timeline as before.
 */
export function gatedHandoffs(thread: Pick<DoorThreadRead, 'handoffs'>): DoorHandoffRead[] {
  return (thread.handoffs ?? []).filter((h) => h.state === 'pending' || h.state === 'held');
}

/** One line for a gated hand-off, in the sender's words. */
export function gatedHandoffLine(h: DoorHandoffRead): string {
  if (h.state === 'held') {
    const points = (h.known_points ?? []).filter(Boolean);
    const why = points.length > 0 ? ` They already have: ${points.join('; ')}.` : '';
    return `Not emailed: Merlin judged ${h.to_name} already knows this.${why}`;
  }
  return `Merlin is checking whether ${h.to_name} already knows this before the email goes out.`;
}

/** An item the sender believes went out but did not: a failed send, or no
 *  send at all. What the alarms count and the list puts first. */
function notReached(
  thread: { id: string } & Partial<Pick<DoorThreadRead, 'route_notified'>>,
  outcomes: Record<string, DeliveryOutcome | null | undefined>,
): boolean {
  return (outcomes[thread.id]?.undelivered.length ?? 0) > 0 || neverEmailed(thread);
}

/** Routed away order: anything not delivered or never emailed first, the
 *  rest as given. */
export function undeliveredFirst<T extends { id: string } & Partial<Pick<DoorThreadRead, 'route_notified'>>>(
  threads: T[],
  outcomes: Record<string, DeliveryOutcome | null | undefined>,
): T[] {
  const bad = (t: T) => (notReached(t, outcomes) ? 0 : 1);
  return threads
    .map((t, i) => ({ t, i }))
    .sort((a, b) => bad(a.t) - bad(b.t) || a.i - b.i)
    .map(({ t }) => t);
}

/** How many of these items have a send that never arrived, or were never
 *  emailed at all (the red marker on "More" and on its Routed away entry,
 *  tabs round). */
export function undeliveredCount(
  threads: ReadonlyArray<{ id: string } & Partial<Pick<DoorThreadRead, 'route_notified'>>>,
  outcomes: Record<string, DeliveryOutcome | null | undefined>,
): number {
  return threads.filter((t) => notReached(t, outcomes)).length;
}
